import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CircleAlert, Pencil, Plus, Save, Send, Trash2, Wand2 } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup, clearLookup } from '../HRContext';
import { errorText } from '../../utils';
import { Input } from '../components/Form';
import StatusPill from '../components/StatusPill';
import { DOC_TYPES } from '../resources';
import { EMPLOYMENT_TYPES, GENDERS, MARITAL_STATUSES, SALARY_TYPES, statusTone, fmtDate, fmtMoney, has, initials, titleCase, today } from '../format';
import { hrSubUrl } from '../catalog';
import Icon from '../Icon';
import Loader from '../components/Loader';
import { FileChip, FilePicker, FileSlot, PhotoCard, UploadActions, openStored } from '../components/Files';
import { DOCUMENT_RULES, documentRule } from '../fileRules';
import { allowedGrades, benefitRows, eligibilityFor, familyOf, gradeOf, outsidePolicy } from '../gradePolicy';

// Create or edit a complete employee onboarding record — the port of Stephanet's Add Employee wizard.
// The nine steps, their sections and required fields follow the reference. Each field is stored in one part of
// the employee record: the core record, personal info, statutory & bank, expatriate details, profile detail,
// compensation, work rules, emergency contacts, dependants, references and documents.

const STATES = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo',
  'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];
export const BANKS = ['Access Bank', 'Citibank', 'Ecobank', 'Fidelity Bank', 'First Bank of Nigeria', 'First City Monument Bank (FCMB)', 'Globus Bank', 'Guaranty Trust Bank (GTBank)',
  'Heritage Bank', 'Jaiz Bank', 'Keystone Bank', 'Kuda Bank', 'Moniepoint MFB', 'Opay', 'Palmpay', 'Polaris Bank', 'Providus Bank', 'Stanbic IBTC Bank',
  'Standard Chartered', 'Sterling Bank', 'SunTrust Bank', 'Titan Trust Bank', 'Union Bank', 'United Bank for Africa (UBA)', 'Unity Bank', 'Wema Bank', 'Zenith Bank'];
export const PFAS = ['ARM Pension Managers', 'AXA Mansard Pensions', 'Crusader Sterling Pensions', 'FCMB Pensions', 'Fidelity Pension Managers', 'Guaranty Trust Pension Managers',
  'Leadway Pensure', 'NLPC Pension Fund Administrators', 'NPF Pensions', 'Oak Pensions', 'Pensions Alliance Limited (PAL)', 'Premium Pension', 'Stanbic IBTC Pension Managers',
  'Tangerine APT Pensions', 'Trustfund Pensions', 'Veritas Glanvills Pensions'];
const RELATIONSHIPS = ['Spouse', 'Father', 'Mother', 'Brother', 'Sister', 'Son', 'Daughter', 'Uncle', 'Aunt', 'Cousin', 'Friend', 'Guardian'];
const JOB_TITLES = ['Accountant', 'Cost Accountant', 'Finance Officer', 'Payroll Officer', 'Tax Officer', 'HR Officer', 'HR Manager', 'Admin Officer', 'Procurement Officer',
  'Store Keeper', 'Site Engineer', 'Civil Engineer', 'Electrical Engineer', 'Mechanical Engineer', 'Quantity Surveyor', 'Project Manager', 'HSE Officer', 'Safety Lead',
  'Supervisor', 'Foreman', 'Technician', 'Welder', 'Driver', 'Operator', 'Labourer', 'Security Officer', 'IT Officer'];
const SECTIONS = ['Head Office Section', 'Project Site Section', 'Field Operations Section', 'Store Section', 'Procurement Section', 'Finance Section', 'HR Section', 'HSE Section'];
const opts = list => list.map(v => ({ value: v, label: v }));
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// field name -> definition
const F = {
  // core identity
  first_name: { label: 'First Name', required: true, placeholder: 'First name' },
  last_name: { label: 'Surname', required: true, placeholder: 'Surname' },
  middle_name: { label: 'Middle Name', placeholder: 'Optional' },
  full_name: { label: 'Full Name (Auto-generated)' },
  gender: { label: 'Gender', type: 'select', options: GENDERS, required: true, placeholder: 'Select gender' },
  date_of_birth: { label: 'Date of Birth', type: 'date', required: true },
  nationality: { label: 'Nationality', suggestions: ['Nigerian', 'Ghanaian', 'British', 'American', 'Indian', 'Chinese', 'Lebanese', 'Filipino', 'South African'] },
  state_of_origin: { label: 'State of Origin', type: 'select', options: opts(STATES), placeholder: 'Select state' },
  lga_of_origin: { label: 'LGA', placeholder: 'Local government area' },
  religion: { label: 'Religion', type: 'select', options: opts(['Christianity', 'Islam', 'Traditional', 'Other']), placeholder: 'Optional' },
  marital_status: { label: 'Marital Status', type: 'select', options: MARITAL_STATUSES, noBlank: true },
  blood_group: { label: 'Blood Group', type: 'select', options: opts(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']), placeholder: 'Optional' },
  genotype: { label: 'Genotype', type: 'select', options: opts(['AA', 'AS', 'AC', 'SS', 'SC']), placeholder: 'Optional' },
  employee_no: { label: 'Employee ID', placeholder: 'Auto-generated if left blank' },
  // expatriate
  expat_class: { label: 'Expatriate Class', type: 'select', options: opts(['Class A', 'Class B', 'Class C', 'Class D']), placeholder: 'Select class' },
  passport_number: { label: 'Passport Number' },
  passport_country: { label: 'Passport Country' },
  passport_expiry_date: { label: 'Passport Expiry', type: 'date' },
  visa_type: { label: 'Visa Type', suggestions: ['STR', 'TWP', 'Business', 'Visitor'] },
  visa_expiry_date: { label: 'Visa Expiry', type: 'date' },
  cerpac_number: { label: 'CERPAC Number' },
  cerpac_expiry_date: { label: 'CERPAC Expiry', type: 'date' },
  quota_slot_reference: { label: 'Expatriate Quota Reference' },
  // login & address
  work_email: { label: 'Work Email', type: 'email', required: true, placeholder: 'name@rifitel.com' },
  personal_email: { label: 'Personal Email', type: 'email', placeholder: 'Optional' },
  residential_address: { label: 'Current Address', type: 'textarea' },
  permanent_address: { label: 'Permanent Address', type: 'textarea' },
  city: { label: 'City' },
  state_of_residence: { label: 'State', type: 'select', options: opts(STATES), placeholder: 'Select state' },
  country: { label: 'Country', suggestions: ['Nigeria', 'Ghana', 'United Kingdom', 'United States', 'India', 'China', 'Lebanon'] },
  postal_code: { label: 'Postal Code' },
  // means of identification
  id_type: { label: 'ID Type', type: 'select', options: opts(['National ID (NIN)', 'International Passport', "Driver's Licence", "Voter's Card"]), placeholder: 'Select ID type' },
  id_number: { label: 'ID Number' },
  id_issue_date: { label: 'Issue Date', type: 'date' },
  id_expiry_date: { label: 'Expiry Date', type: 'date' },
  // organisational assignment
  department_id: { label: 'Department', type: 'ref', ref: 'departments', required: true },
  designation_id: { label: 'Designation', type: 'ref', ref: 'designations', required: true },
  job_title: { label: 'Job Title', suggestions: JOB_TITLES },
  unit: { label: 'Unit' },
  section: { label: 'Section', suggestions: SECTIONS },
  team_number: { label: 'Team Number', placeholder: 'e.g. Team 004' },
  branch_id: { label: 'Branch', type: 'ref', ref: 'branches' },
  reporting_office: { label: 'Reporting Office', placeholder: 'e.g. Abuja Head Office' },
  staff_category: { label: 'Staff Category', type: 'select', options: opts(['Senior Staff', 'Junior Staff', 'Field Staff', 'Contract Staff', 'Intern']), placeholder: 'Select category' },
  // employment details
  hire_date: { label: 'Date of Employment', type: 'date', required: true },
  date_of_join: { label: 'Date of Joining', type: 'date' },
  employment_type: { label: 'Employment Type', type: 'select', options: EMPLOYMENT_TYPES, required: true, noBlank: true },
  management_type: { label: 'Employee Management Type', type: 'select', options: opts(['Management Employee', 'Non-Management Employee']), noBlank: true },
  employee_level: { label: 'Employee Level', type: 'select', options: opts(['Entry Level', 'Intermediate', 'Senior', 'Lead', 'Executive']), noBlank: true },
  reports_to_id: { label: 'Supervisor / Line Manager', type: 'ref', ref: 'employees' },
  // operational assignment
  primary_site_id: { label: 'Primary Project Site', type: 'ref', ref: 'sites' },
  company_site: { label: 'Company Site / Location' },
  site_access_level: { label: 'Site Access Level', type: 'select', options: opts(['Standard', 'Restricted', 'Full Access', 'Visitor']), placeholder: 'Select level' },
  // probation
  probation_start_date: { label: 'Probation Start', type: 'date' },
  probation_end_date: { label: 'Probation End', type: 'date' },
  probation_months: { label: 'Duration (months)', type: 'integer', min: 0, max: 36 },
  probation_department_id: { label: 'Probation Department', type: 'ref', ref: 'departments' },
  post_probation_department_id: { label: 'Department After Probation', type: 'ref', ref: 'departments' },
  probation_reviewer_id: { label: 'Probation Reviewer', type: 'ref', ref: 'employees' },
  confirmation_due_date: { label: 'Confirmation Due', type: 'date' },
  confirmation_date: { label: 'Confirmation Date', type: 'date' },
  // PPE
  ppe_size: { label: 'PPE / Boot Size' },
  uniform_size: { label: 'Uniform Size', type: 'select', options: opts(['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL']), placeholder: 'Select size' },
  ppe_issue_date: { label: 'Date Issued', type: 'date' },
  ppe_issued_by: { label: 'Issued By' },
  ppe_remarks: { label: 'PPE Remarks', type: 'textarea' },
  // contact
  work_phone: { label: 'Phone Number 1', required: true, placeholder: '080…' },
  personal_phone: { label: 'Phone Number 2', placeholder: 'Optional' },
  whatsapp_number: { label: 'WhatsApp Number' },
  emergency_phone: { label: 'Emergency Phone' },
  preferred_contact: { label: 'Preferred Contact', type: 'select', options: opts(['Phone', 'WhatsApp', 'Email', 'SMS']), noBlank: true },
  references: { label: 'Professional References 1 & 2' },
  // academic
  highest_qualification: { label: 'Highest Qualification', type: 'select', options: opts(['SSCE', 'OND', 'HND', 'B.Sc / B.Eng / B.A', 'PGD', 'M.Sc / MBA', 'PhD', 'Trade Test', 'Other']), placeholder: 'Select' },
  course_of_study: { label: 'Course of Study' },
  institution: { label: 'Institution' },
  graduation_year: { label: 'Year Graduated', type: 'integer', min: 1950, max: 2100 },
  professional_certification: { label: 'Professional Certification', placeholder: 'e.g. COREN, ICAN, NEBOSH' },
  skills: { label: 'Skills', type: 'textarea' },
  // salary configuration
  salary_type: { label: 'Salary Type', type: 'select', options: SALARY_TYPES, required: true, noBlank: true },
  salary_mode: { label: 'Salary Mode', type: 'select', options: [{ value: 'grade', label: 'Salary grade / pay scale' }, { value: 'manual', label: 'Manual configuration' }], noBlank: true },
  // category, family, position and grade follow Payroll › Grade Policy Setup (options filled in from it below)
  employee_category: { label: 'Employee Category', type: 'select', options: opts(['Permanent Staff', 'Contract Staff', 'Casual Worker', 'Intern', 'Expatriate', 'Consultant', 'Field Worker', 'Management Staff']), placeholder: 'Select category' },
  job_family: { label: 'Job Family', type: 'select', options: [], placeholder: 'Select job family' },
  job_position: { label: 'Position', type: 'select', options: [], placeholder: 'Select position' },
  pay_grade: { label: 'Salary Grade / Pay Scale', type: 'select', options: [], placeholder: 'Select grade' },
  grade_exception_reason: { label: 'Reason for Manual Setup / Grade Exception', type: 'textarea' },
  basic_salary: { label: 'Base Salary (monthly)', type: 'money', required: true },
  daily_rate: { label: 'Daily Rate', type: 'money', required: true },
  next_review_date: { label: 'Next Review Date', type: 'date' },
  housing_allowance: { label: 'Housing Allowance', type: 'money' },
  transport_allowance: { label: 'Transport Allowance', type: 'money' },
  meal_allowance: { label: 'Meal Allowance', type: 'money' },
  utility_allowance: { label: 'Utility Allowance', type: 'money' },
  medical_allowance: { label: 'Medical Allowance', type: 'money' },
  other_allowance: { label: 'Other Allowance', type: 'money' },
  overtime_rate: { label: 'Overtime Rate (× hourly)', type: 'number', min: 0, max: 10, placeholder: 'e.g. 1.5' },
  overtime_threshold_hours: { label: 'Overtime After (hours/day)', type: 'number', min: 0, max: 24 },
  pension_employee_rate: { label: 'Employee Pension (%)', type: 'number', min: 0, max: 100, placeholder: '8' },
  pension_employer_rate: { label: 'Employer Pension (%)', type: 'number', min: 0, max: 100, placeholder: '10' },
  // payment & statutory
  payment_method: { label: 'Payment Method', type: 'select', options: opts(['Bank Transfer', 'Cash', 'Cheque']), noBlank: true },
  bank_name: { label: 'Bank Name', type: 'select', options: opts(BANKS), required: true, placeholder: 'Select bank' },
  bank_account_name: { label: 'Account Name', required: true },
  bank_account_number: { label: 'Account Number', required: true, maxLength: 10, placeholder: '10-digit NUBAN' },
  bank_branch: { label: 'Bank Branch' },
  bank_sort_code: { label: 'Bank / Sort Code' },
  bvn: { label: 'BVN', maxLength: 11 },
  pension_fund_administrator: { label: 'Pension Provider / PFA', type: 'select', options: opts(PFAS), placeholder: 'Select PFA' },
  pension_rsa_pin: { label: 'Pension ID / RSA Number', placeholder: 'PEN…' },
  tax_identification_number: { label: 'Tax Identification Number (TIN)' },
  nhf_number: { label: 'NHF Number' },
  nhia_number: { label: 'NHIA Number' },
  nsitf_number: { label: 'NSITF Number' },
  itf_number: { label: 'ITF Number' },
  hmo_provider: { label: 'HMO Provider', suggestions: ['Hygeia HMO', 'AXA Mansard', 'Reliance HMO', 'Leadway Health', 'Avon HMO', 'Clearline HMO'] },
  hmo_id: { label: 'HMO ID / Health Plan Number' },
  hr_notes: { label: 'HR Notes', type: 'textarea' },
  onboarding_remark: { label: 'Onboarding Remark', type: 'textarea' },
  payroll_remark: { label: 'Payroll Remark', type: 'textarea' },
  // work rules & leave
  work_rules_source: { label: 'Work Rules Source', type: 'select', options: [{ value: 'grade', label: 'Salary grade policy' }, { value: 'manual', label: 'Manual setup' }], noBlank: true },
  work_rules_override_reason: { label: 'Override Reason (required for manual setup)', type: 'textarea' },
  shift_name: { label: 'Shift Name', placeholder: 'e.g. Day Shift A' },
  shift_type: { label: 'Shift Type', type: 'select', options: opts(['Day', 'Night', 'Rotational', 'Flexible']), placeholder: 'Select type' },
  work_pattern: { label: 'Work Pattern', suggestions: ['5 on / 2 off', '6 on / 1 off', '21/7 rotation', '14/14 rotation'] },
  days_per_week: { label: 'Days per Week', type: 'integer', min: 0, max: 7 },
  daily_hours: { label: 'Daily Hours', type: 'number', min: 0, max: 24 },
  shift_start: { label: 'Shift Start', type: 'time' },
  shift_end: { label: 'Shift End', type: 'time' },
  attendance_method: { label: 'Attendance Capture', type: 'select', options: opts(['Biometric', 'Manual Register', 'Mobile Check-in', 'Access Card']), placeholder: 'Select method' },
  annual_leave_days: { label: 'Annual Leave (days)', type: 'integer', min: 0 },
  sick_leave_days: { label: 'Sick Leave (days)', type: 'integer', min: 0 },
  casual_leave_days: { label: 'Casual Leave (days)', type: 'integer', min: 0 },
  maternity_leave_days: { label: 'Maternity Leave (days)', type: 'integer', min: 0 },
  paternity_leave_days: { label: 'Paternity Leave (days)', type: 'integer', min: 0 },
  compassionate_leave_days: { label: 'Compassionate Leave (days)', type: 'integer', min: 0 },
  max_carry_forward_days: { label: 'Max Carry Forward (days)', type: 'integer', min: 0 },
  leave_accrual_method: { label: 'Leave Accrual', type: 'select', options: opts(['Annual', 'Monthly', 'Pro-rata']), placeholder: 'Select method' },
};
Object.entries(F).forEach(([name, f]) => { f.name = name; });

// which part of the record each field lives in (the payload of its save call)
const MASTER = ['employee_no', 'first_name', 'middle_name', 'last_name', 'work_email', 'work_phone', 'employment_type', 'salary_type', 'hire_date',
  'probation_end_date', 'confirmation_date', 'branch_id', 'department_id', 'designation_id', 'primary_site_id', 'reports_to_id', 'basic_salary', 'is_private'];
const PERSONAL = ['gender', 'date_of_birth', 'marital_status', 'nationality', 'state_of_origin', 'lga_of_origin', 'religion', 'blood_group', 'genotype',
  'personal_email', 'personal_phone', 'residential_address'];
const BANK = ['bank_name', 'bank_account_number', 'bank_account_name', 'bank_sort_code', 'bvn', 'tax_identification_number', 'pension_rsa_pin', 'pension_fund_administrator', 'nhf_number'];
const EXPAT = ['is_expatriate', 'expat_class', 'passport_number', 'passport_country', 'passport_expiry_date', 'visa_type', 'visa_expiry_date', 'cerpac_number', 'cerpac_expiry_date', 'quota_slot_reference'];
const PPE_ITEMS = [['issued_safety_shoe', 'Safety Shoe'], ['issued_coverall', 'Coverall'], ['issued_allied_equipment', 'Allied Equipment'],
  ['issued_id_card', 'ID Card'], ['issued_laptop', 'Laptop'], ['issued_toolkit', 'Toolkit']];
const PROFILE = ['id_type', 'id_number', 'id_issue_date', 'id_expiry_date', 'permanent_address', 'city', 'state_of_residence', 'country', 'postal_code',
  'whatsapp_number', 'emergency_phone', 'preferred_contact', 'job_title', 'unit', 'section', 'team_number', 'reporting_office', 'staff_category',
  'management_type', 'employee_level', 'is_permanent', 'date_of_join', 'company_site', 'site_access_level', 'head_office_based', 'cover_all_sites',
  'probation_start_date', 'probation_months', 'probation_department_id', 'post_probation_department_id', 'probation_reviewer_id', 'confirmation_due_date',
  ...PPE_ITEMS.map(([n]) => n), 'ppe_size', 'uniform_size', 'ppe_issue_date', 'ppe_issued_by', 'ppe_remarks',
  'highest_qualification', 'course_of_study', 'institution', 'graduation_year', 'professional_certification', 'skills', 'hr_notes', 'onboarding_remark', 'payroll_remark'];
const ALLOWANCES = ['housing_allowance', 'transport_allowance', 'meal_allowance', 'utility_allowance', 'medical_allowance', 'other_allowance'];
const COMP = ['salary_mode', 'employee_category', 'job_family', 'job_position', 'pay_grade', 'grade_exception_reason', 'daily_rate', 'salary_on_review', 'next_review_date',
  ...ALLOWANCES, 'overtime_eligible', 'overtime_rate', 'overtime_threshold_hours', 'bonus_eligible', 'leave_allowance_eligible', 'include_in_pension', 'include_in_paye',
  'pension_employee_rate', 'pension_employer_rate', 'payment_method', 'bank_branch', 'nhia_number', 'nsitf_number', 'itf_number', 'hmo_provider', 'hmo_id'];
const RULES = ['work_rules_source', 'work_rules_override_reason', 'shift_name', 'shift_type', 'work_pattern', 'work_days', 'days_per_week', 'daily_hours', 'shift_start',
  'shift_end', 'attendance_method', 'annual_leave_days', 'sick_leave_days', 'casual_leave_days', 'maternity_leave_days', 'paternity_leave_days', 'compassionate_leave_days',
  'carry_forward_allowed', 'max_carry_forward_days', 'leave_accrual_method'];
const NUMERIC = new Set(['basic_salary', 'daily_rate', ...ALLOWANCES, 'overtime_rate', 'overtime_threshold_hours', 'pension_employee_rate', 'pension_employer_rate',
  'probation_months', 'graduation_year', 'days_per_week', 'daily_hours', 'annual_leave_days', 'sick_leave_days', 'casual_leave_days', 'maternity_leave_days',
  'paternity_leave_days', 'compassionate_leave_days', 'max_carry_forward_days']);

// required fields per step; `onboarding` is true for a new or draft record (the reference relaxes some rules for existing staff)
export const STEPS = [
  // Existing staff (many imported from user accounts with gaps) can be saved with partial data;
  // the full requirements apply while onboarding. Complete Employee Records lists what is still missing.
  { key: 'personal', title: 'Personal Details', icon: 'user-round', desc: 'Employee identity, contact identity, address and system access',
    required: (d, onboarding) => (onboarding ? ['first_name', 'last_name', 'gender', 'date_of_birth', 'work_email'] : ['first_name', 'last_name']) },
  { key: 'company', title: 'Company Details', icon: 'building-2', desc: 'Organisational assignment, employment terms, sites, probation and PPE',
    required: (d, onboarding) => (onboarding ? ['department_id', 'designation_id', 'hire_date', 'employment_type'] : ['employment_type']) },
  { key: 'contact', title: 'Contact & References', icon: 'phone', desc: 'Phone numbers, preferred contact method and professional references',
    required: (d, onboarding) => (onboarding ? ['work_phone', 'references'] : ['work_phone']) },
  { key: 'academic', title: 'Academic & Family', heading: 'Academic & Family Details', icon: 'graduation-cap', desc: 'Educational background, certifications, family information and next of kin',
    required: () => [] },
  { key: 'financial', title: 'Financial Details', icon: 'wallet', desc: 'Salary structure, allowances, overtime, pension, tax and grade configuration',
    required: (d, onboarding) => (onboarding ? ['salary_type', d.salary_type === 'daily' ? 'daily_rate' : 'basic_salary', ...(d.salary_mode === 'manual' ? ['grade_exception_reason'] : [])] : ['salary_type']) },
  { key: 'payment', title: 'Payment & Statutory', heading: 'Payment & Statutory Details', icon: 'landmark', desc: 'Bank account, payment method, pension, HMO and government statutory numbers',
    required: (d, onboarding) => (onboarding && (d.payment_method || 'Bank Transfer') === 'Bank Transfer' ? ['bank_name', 'bank_account_name', 'bank_account_number'] : []) },
  { key: 'work', title: 'Work Rules & Leave', heading: 'Work Rules & Leave Setup', icon: 'calendar-clock', desc: 'Configure shift, attendance capture method and leave entitlements',
    required: d => (d.work_rules_source === 'manual' ? ['work_rules_override_reason'] : []) },
  { key: 'documents', title: 'Documents', heading: 'Documents Upload', icon: 'file-badge', desc: 'Record onboarding documents and any documents that expire', required: () => [] },
  { key: 'review', title: 'Review & Submit', icon: 'clipboard-check', desc: 'Check the record, then submit it', required: () => [] },
];
const FORM_STEPS = STEPS.filter(s => s.key !== 'review');

const DEFAULTS = {
  nationality: 'Nigerian', country: 'Nigeria', marital_status: 'single', employment_type: 'full_time', salary_type: 'monthly', is_private: false, is_expatriate: false,
  hire_date: today(), preferred_contact: 'Phone', management_type: 'Non-Management Employee', employee_level: 'Entry Level', is_permanent: false,
  salary_mode: 'grade', payment_method: 'Bank Transfer', salary_on_review: false, overtime_eligible: true, bonus_eligible: true, leave_allowance_eligible: true,
  include_in_pension: true, include_in_paye: true, pension_employee_rate: 8, pension_employer_rate: 10, work_rules_source: 'grade', carry_forward_allowed: false,
  head_office_based: false, cover_all_sites: false, ...Object.fromEntries(PPE_ITEMS.map(([n]) => [n, false])),
};
const blank = (f, v) => !has(v) || (f?.type === 'money' && Number(v) === 0);
// a section's payload: blanks become null, numbers become numbers, booleans stay as set
const section = (names, d) => Object.fromEntries(names.filter(n => d[n] !== undefined).map(n => {
  const v = d[n];
  if (v === '' || v === null) return [n, null];
  return [n, NUMERIC.has(n) ? Number(v) : v];
}));
const totalAllowances = d => ALLOWANCES.reduce((t, n) => t + (Number(d[n]) || 0), 0);

function SectionHead({ icon, title, hint }) {
  return (
    <div className="sec-head">
      <span className="sec-icon"><Icon name={icon} size={17} /></span>
      <div><h4>{title}</h4>{hint && <p className="muted small">{hint}</p>}</div>
    </div>
  );
}

function Toggle({ title, desc, checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} className={`toggle-card ${checked ? 'on' : ''}`} onClick={() => onChange(!checked)}>
      <span><b>{title}</b><small>{desc}</small></span>
      <span className="switch-ui" aria-hidden="true" />
    </button>
  );
}

// editable list of child records (emergency contacts, dependants, references, documents)
function ListEditor({ rows, setRows, columns, blankRow, addLabel, empty, max, heading }) {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <>
      {rows.length === 0 && <div className="empty">{empty}</div>}
      {rows.map((r, i) => (
        <div key={r.id || `new-${i}`} className="wiz-list-item">
          {heading && <div className="muted small"><b>{heading(i, r)}</b></div>}
          <div className="form-grid cols-3 wiz-list-row">
            {columns.map(c => (
              <div key={c.name} className={`field ${c.full ? 'full' : ''}`}>
                <span className="field-label">{c.label}{c.required && <em>*</em>}</span>
                <Input field={c} value={r[c.name]} onChange={v => set(i, c.name, v)} data={r} />
              </div>
            ))}
            <div className="field"><span className="field-label">&nbsp;</span>
              <button type="button" className="btn danger ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={14} /> Remove</button>
            </div>
          </div>
        </div>
      ))}
      {(!max || rows.length < max) && <button type="button" className="btn" onClick={() => setRows([...rows, { ...blankRow }])}><Plus size={15} /> {addLabel}</button>}
    </>
  );
}

const CONTACT_COLS = [
  { name: 'contact_name', label: 'Full Name', required: true },
  { name: 'relationship_type', label: 'Relationship', required: true, suggestions: RELATIONSHIPS },
  { name: 'phone_primary', label: 'Phone', required: true },
  { name: 'phone_secondary', label: 'Alternative Phone' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'address', label: 'Address' },
  { name: 'is_next_of_kin', label: 'Next of Kin', type: 'boolean' },
  { name: 'is_primary_contact', label: 'Primary Contact', type: 'boolean' },
];
const REFERENCE_COLS = [
  { name: 'full_name', label: 'Full Name', required: true },
  { name: 'phone', label: 'Phone', required: true },
  { name: 'relationship_type', label: 'Relationship', suggestions: ['Former Employer', 'Former Supervisor', 'Colleague', 'Lecturer', 'Guarantor', 'Community Leader'] },
  { name: 'company', label: 'Company / Organisation' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'address', label: 'Address' },
];
const DEPENDANT_COLS = [
  { name: 'full_name', label: 'Full Name', required: true },
  { name: 'relationship_type', label: 'Relationship', required: true, suggestions: ['Spouse', 'Son', 'Daughter', 'Ward', 'Parent', 'Sibling'] },
  { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  { name: 'gender', label: 'Gender', type: 'select', options: GENDERS },
  { name: 'hmo_covered', label: 'HMO Covered', type: 'boolean' },
  { name: 'notes', label: 'Notes' },
];
const DOC_COLS = [
  { name: 'document_type', label: 'Document Type', type: 'select', options: DOC_TYPES, required: true },
  { name: 'title', label: 'Title', required: true },
  { name: 'document_number', label: 'Document Number' },
  { name: 'issuing_authority', label: 'Issuing Authority' },
  { name: 'issue_date', label: 'Issue Date', type: 'date' },
  { name: 'expiry_date', label: 'Expiry Date', type: 'date' },
  { name: 'file', label: 'Scan', type: 'file', rule: d => documentRule(d.document_type) },
  { name: 'requires_renewal', label: 'Requires Renewal', type: 'boolean' },
];
// required onboarding documents, as the reference checklist
export const ONBOARDING_DOCS = [
  ['cv', 'CV / Résumé', 'file-user'], ['certificate', 'Academic Certificates', 'graduation-cap'], ['offer_letter', 'Offer Letter', 'mail-open'],
  ['contract', 'Signed Contract', 'pen-line'], ['guarantor_form', 'Guarantor Form', 'handshake'], ['id_card', 'Means of Identification', 'id-card'],
  ['medical', 'Medical Fitness', 'heart-pulse'],
];
// file slots per document type; CV, ID and certificates also have slots on their own steps (same state)
const SLOT_ELSEWHERE = new Set(['cv', 'id_card', 'certificate']);
const NO_FILES = { photo: null, certificate: [], ...Object.fromEntries(ONBOARDING_DOCS.filter(([t]) => t !== 'certificate').map(([t]) => [t, null])) };
// leave policy (Leave Management → leave types) by entitlement field
const POLICY_LEAVE = [['annual_leave_days', /annual/i], ['sick_leave_days', /sick/i], ['casual_leave_days', /casual/i],
  ['maternity_leave_days', /maternity/i], ['paternity_leave_days', /paternity/i], ['compassionate_leave_days', /compassion|bereave/i]];

const flatten = e => ({
  ...DEFAULTS,
  ...e,
  ...(e.personal_info || {}),
  ...(e.statutory_and_bank || {}),
  ...(e.expatriate_detail || {}),
  ...(e.profile_detail || {}),
  ...(e.compensation || {}),
  ...(e.work_rules || {}),
  id: e.id,
  is_expatriate: !!e.expatriate_detail?.is_expatriate,
});

export default function AddEmployee() {
  const { canManage, notify, confirm } = useHR();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const id = params.get('id') || '';
  const isNew = !id;
  const [step, setStep] = useState(0);
  const [record, setRecord] = useState(null);
  const [data, setData] = useState(DEFAULTS);
  const [contacts, setContacts] = useState([]);
  const [references, setReferences] = useState([]);
  const [otherDepts, setOtherDepts] = useState([]);
  const [dependants, setDependants] = useState([]);
  const [docs, setDocs] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  // files picked in the wizard, uploaded once the record is saved; storedFiles = what the server already has
  const [files, setFiles] = useState(NO_FILES);
  const [storedFiles, setStoredFiles] = useState([]);
  const [startAs, setStartAs] = useState('probation');
  const [visited, setVisited] = useState(() => new Set([0]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const leaveTypes = useLookup('leaveTypes') || [];
  const [policy, setPolicy] = useState(null);   // Payroll › Grade Policy Setup
  useEffect(() => { hr.gradeSetup().then(r => setPolicy(Array.isArray(r?.families) ? r : null)).catch(() => setPolicy(null)); }, []);
  const options = {
    departments: useLookup('departments'), designations: useLookup('designations'), sites: useLookup('sites'), branches: useLookup('branches'), employees: useLookup('employees'),
  };

  const load = e => {
    setRecord(e); setData(flatten(e)); setContacts(e.emergency_contacts || []); setDependants(e.dependents || []);
    setReferences([...(e.references || [])].sort((a, b) => a.position - b.position));
    setOtherDepts((e.departments || []).filter(x => !x.is_primary && String(x.id) !== String(e.department_id)).map(x => String(x.id)));
  };
  useEffect(() => {
    setError(''); setStep(0); setVisited(new Set([0]));
    setFiles(NO_FILES);
    if (isNew) { setRecord(null); setData(DEFAULTS); setContacts([]); setReferences([]); setOtherDepts([]); setDependants([]); setDocs([]); setExistingDocs([]); setStoredFiles([]); return; }
    hr.getEmployee(id).then(load).catch(err => setError(errorText(err)));
    hr.listEmployeeDocuments(id).then(setExistingDocs).catch(() => setExistingDocs([]));
    hr.listEmployeeFiles(id).then(setStoredFiles).catch(() => setStoredFiles([]));
  }, [id, isNew]);

  const setFile = (k, v) => setFiles(f => ({ ...f, [k]: v }));
  // an existing employee's file goes up the moment it is picked (it never waits on the rest of the form,
  // which may still have required gaps); a new employee's files wait until the record is first saved
  const [uploading, setUploading] = useState('');
  const [uploadError, setUploadError] = useState('');
  const uploadNow = async (label, fn) => {
    setUploading(label); setUploadError('');
    try { await fn(); await reloadFiles(); notify(`${label} uploaded`); }
    catch (err) { setUploadError(`${label} was not uploaded — ${errorText(err)}`); }
    setUploading('');
  };
  const uploadDoc = async (type, label, file) => {
    const doc = existingDocs.find(x => x.document_type === type) || await hr.createDocument({ employee_id: id, document_type: type, title: label, requires_renewal: false });
    await hr.uploadDocumentFile(doc.id, file);
  };
  // picking only stages a file (preview / chip); an existing employee's staged file goes up when its
  // Upload button is clicked, a new employee's when the record is first saved
  const pickFile = (type, value) => setFile(type, value);
  const uploadStaged = type => {
    const value = files[type];
    const label = ONBOARDING_DOCS.find(([t]) => t === type)?.[1] || 'Photo';
    const done = () => setFile(type, type === 'certificate' ? [] : null);
    const run = fn => uploadNow(label, async () => { await fn(); done(); });
    if (type === 'photo') return run(() => hr.uploadPhoto(id, value));
    if (type === 'cv') return run(() => hr.uploadCv(id, value));
    if (type === 'id_card') return run(() => hr.uploadIdDocument(id, value));
    if (type === 'certificate') return run(async () => { for (const f of value) await hr.uploadCertificate(id, f, f.name.replace(/\.[^.]+$/, '')); });
    return run(() => uploadDoc(type, label, value));
  };
  const stagedUpload = type => (isNew ? undefined : () => uploadStaged(type));

  const reloadFiles = async () => {
    const [d, f] = await Promise.all([hr.listEmployeeDocuments(id).catch(() => []), hr.listEmployeeFiles(id).catch(() => [])]);
    setExistingDocs(d); setStoredFiles(f);
  };
  const storedOf = type => storedFiles.filter(f => f.document_type === type);
  const storedPhoto = storedFiles.find(f => f.category === 'photo');
  const removeStored = async f => {
    const what = f.category === 'photo' ? 'profile photo' : 'file';
    if (!(await confirm({ title: f.category === 'photo' ? 'Remove Photo' : 'Remove File', message: `Remove this ${what}?`, detail: `${f.name} will be deleted from the server. This cannot be undone.`, confirmLabel: 'Remove', danger: true }))) return;
    try { await (f.category === 'photo' ? hr.deletePhoto(id) : hr.deleteDocumentFile(f.document_id)); await reloadFiles(); notify('File removed'); }
    catch (err) { setError(errorText(err)); }
  };
  const openStoredFile = f => openStored(() => hr.documentFileBlob(f.document_id));
  // one document slot: what is on file for this type plus the file waiting to be uploaded
  const docSlot = (type, label) => (
    <FileSlot label={label} rule={documentRule(type)} stored={storedOf(type)[0]} pending={files[type]}
              onPick={f => pickFile(type, f)} onClear={() => setFile(type, null)} onUpload={stagedUpload(type)} busy={!!uploading} onOpen={() => openStoredFile(storedOf(type)[0])} onDelete={() => removeStored(storedOf(type)[0])} />
  );
  const certificateSlot = (
    <div className="file-slot">
      <span className="field-label">Academic Certificates</span>
      <div className="file-slot-body">
        {storedOf('certificate').map(f => <FileChip key={f.id} name={f.name} size={f.size} onOpen={() => openStoredFile(f)} onRemove={() => removeStored(f)} />)}
        {files.certificate.map((f, i) => <FileChip key={`p${i}`} name={f.name} size={f.size} pending pendingNote={isNew ? undefined : 'not uploaded yet'} onRemove={() => setFile('certificate', files.certificate.filter((_, j) => j !== i))} />)}
        {!isNew && files.certificate.length > 0 && (
          <UploadActions busy={!!uploading} onUpload={() => uploadStaged('certificate')} onRemove={() => setFile('certificate', [])}
                         label={`Upload ${files.certificate.length} certificate${files.certificate.length > 1 ? 's' : ''}`} />
        )}
        <FilePicker rule={DOCUMENT_RULES.certificate} multiple label={files.certificate.length || storedOf('certificate').length ? 'Add more' : 'Choose files'} onPick={list => setFile('certificate', [...files.certificate, ...list])} />
      </div>
    </div>
  );

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const fullName = [data.first_name, data.middle_name, data.last_name].filter(Boolean).join(' ');
  const isDraft = record && ['draft', 'onboarding'].includes(record.employment_status);
  const onboarding = isNew || isDraft;
  const current = STEPS[step];
  const goodRefs = references.filter(r => has(r.full_name) && has(r.phone)).length;
  const missingIn = s => s.required(data, onboarding).filter(n => (n === 'references' ? goodRefs < 2 : blank(F[n], data[n])));

  // completion per step, as the reference stepper shows it
  const complete = i => {
    const s = STEPS[i];
    if (s.key === 'review' || missingIn(s).length) return false;
    return s.required(data, onboarding).length > 0 || visited.has(i) || !isNew;
  };
  const stepState = i => {
    if (i === step) return 'current';
    if (STEPS[i].key === 'review') return 'pending';
    if (complete(i)) return 'done';
    return missingIn(STEPS[i]).length && (visited.has(i) || !isNew) ? 'missing' : 'pending';
  };
  const stateLabel = { current: 'Current', done: 'Complete', missing: 'Needs attention', pending: 'Pending' };
  const doneSteps = FORM_STEPS.filter(s => complete(STEPS.indexOf(s)));
  const percent = Math.round((doneSteps.length / FORM_STEPS.length) * 100);
  const allMissing = FORM_STEPS.flatMap(s => missingIn(s).map(name => ({ name, step: STEPS.indexOf(s) })));
  const go = i => { setVisited(v => new Set([...v, i])); setStep(i); window.scrollTo?.(0, 0); };

  const label = (n, v) => {
    const f = F[n];
    if (!has(v)) return null;
    if (f?.type === 'ref') {
      const row = (options[f.ref] || []).find(r => String(r.id) === String(v));
      return row ? (row.full_name || row.name || row.title) : null;
    }
    if (f?.type === 'money') return fmtMoney(v);
    if (f?.type === 'date') return fmtDate(v);
    if (f?.type === 'select') return (f.options.find(o => (o.value ?? o) === v)?.label) || titleCase(v);
    return String(v);
  };

  // Grade Policy Setup: family → its positions → the grades the category + position may be on (others marked, and they need a reason)
  const rule = eligibilityFor(policy, data);
  const allowed = allowedGrades(policy, rule);
  const outside = outsidePolicy(policy, data);
  const fam = familyOf(policy, data.job_family);
  const FX = policy ? {
    job_family: { ...F.job_family, options: opts(policy.families.filter(x => x.active || x.name === data.job_family).map(x => x.name)) },
    job_position: { ...F.job_position, options: opts(policy.positions.filter(x => (!fam || x.family_id === fam.id) && (x.active || x.name === data.job_position)).map(x => x.name)),
      placeholder: fam ? 'Select position' : 'Select the job family first' },
    pay_grade: { ...F.pay_grade, options: policy.grades.map(g => ({ value: g.code, label: `${g.code}${g.name && g.name !== g.code ? ` — ${g.name}` : ''}${allowed && !allowed.some(a => a.id === g.id) ? ' (outside policy)' : ''}` })) },
    grade_exception_reason: { ...F.grade_exception_reason, required: !!outside },
  } : {};
  const pickFamily = v => setData(d => ({ ...d, job_family: v, job_position: policy?.positions.some(x => x.name === d.job_position && x.family_id === familyOf(policy, v)?.id) ? d.job_position : '' }));
  // a newly matched policy fills in its default grade when none is chosen yet
  useEffect(() => {
    if (rule?.default_grade && !data.pay_grade) setData(d => ({ ...d, pay_grade: rule.default_grade }));
  }, [rule?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderField = n => {
    const f = FX[n] || F[n];
    if (n === 'full_name') {
      return (
        <div key={n} className="field"><span className="field-label">{f.label}</span>
          <input value={fullName} placeholder="Filled from the name fields" readOnly /><small className="muted small">Auto-generated from name fields</small></div>
      );
    }
    const required = current.required(data, onboarding).includes(n) || f.required;
    const field = { ...f, required: false, ...(f.type === 'ref' && f.ref === 'employees' ? { filterOptions: o => o.value !== String(id) } : {}) };
    return (
      <div key={n} className={`field ${f.type === 'textarea' ? 'full' : ''} ${error && required && blank(f, data[n]) ? 'invalid' : ''}`}>
        <span className="field-label">{f.label}{required && <em>*</em>}</span>
        <Input field={field} value={data[n]} onChange={v => (n === 'job_family' ? pickFamily(v) : set(n, v))} />
      </div>
    );
  };
  const grid = (names, cols = 3) => <div className={`form-grid cols-${cols}`}>{names.map(renderField)}</div>;

  const applyLeavePolicy = () => {
    const filled = {};
    POLICY_LEAVE.forEach(([field, re]) => { const t = leaveTypes.find(x => x.is_active && re.test(x.name)); if (t) filled[field] = t.days_allowed; });
    const annual = leaveTypes.find(x => /annual/i.test(x.name));
    if (annual) { filled.carry_forward_allowed = annual.max_carry_forward > 0; filled.max_carry_forward_days = annual.max_carry_forward; }
    setData(d => ({ ...d, ...filled }));
  };

  const save = async mode => {
    if (mode !== 'draft' && allMissing.length) { setError(`${allMissing.length} required field(s) missing — see the list on Review & Submit.`); go(STEPS.length - 1); return; }
    if (mode === 'draft' && (!has(data.first_name) || !has(data.last_name) || !has(data.hire_date))) { setError('A draft needs at least the first name, surname and date of employment.'); return; }
    setBusy(true); setError('');
    const status = mode === 'draft' ? 'draft' : mode === 'approval' ? 'onboarding' : startAs;
    try {
      const master = section(MASTER, data);
      if (master.basic_salary === null) delete master.basic_salary;
      const personal = section(PERSONAL, data);
      if (!personal.gender) delete personal.gender; // the backend keeps its default until one is chosen
      const bank = section(BANK, data);
      const expat = section(EXPAT, data);
      const profile = section(PROFILE, data);
      const comp = section(COMP, data);
      ALLOWANCES.forEach(n => { if (comp[n] === null) comp[n] = 0; });
      const rules = section(RULES, data);
      const cleanContacts = contacts.filter(c => has(c.contact_name) && has(c.phone_primary));
      const cleanRefs = references.filter(r => has(r.full_name)).map((r, i) => ({ ...r, position: i + 1 }));
      const cleanDeps = dependants.filter(c => has(c.full_name));
      let empId = id;
      if (isNew) {
        const created = await hr.createEmployee({
          ...master, employment_status: status,
          personal_info: personal,
          statutory_and_bank: Object.values(bank).some(has) ? bank : null,
          expatriate_detail: data.is_expatriate ? expat : null,
          profile_detail: profile, compensation: comp, work_rules: rules,
          emergency_contacts: cleanContacts.map(nonBlankAll), dependents: cleanDeps.map(nonBlankAll), references: cleanRefs.map(nonBlankAll),
        });
        empId = created.id;
      } else {
        await hr.updateEmployee(id, { ...master, ...(isDraft && mode !== 'draft' ? { employment_status: status } : {}) });
        // personal info needs a gender before its row can exist (imported staff may not have one yet)
        if (personal.gender || record?.personal_info) await hr.updatePersonalInfo(id, personal);
        if (Object.values(bank).some(has)) await hr.updateStatutory(id, bank);
        if (data.is_expatriate || record?.expatriate_detail) await hr.updateExpatriate(id, expat);
        await hr.updateProfileDetail(id, profile);
        await hr.updateCompensation(id, comp);
        await hr.updateWorkRules(id, rules);
        await syncList(record.emergency_contacts || [], cleanContacts, {
          add: b => hr.addEmergencyContact(id, b), update: (cid, b) => hr.updateEmergencyContact(cid, b), remove: cid => hr.deleteEmergencyContact(cid),
        });
        await syncList(record.dependents || [], cleanDeps, {
          add: b => hr.addDependent(id, b), update: (did, b) => hr.updateDependent(did, b), remove: did => hr.deleteDependent(did),
        });
        await syncList(record.references || [], cleanRefs, {
          add: b => hr.addReference(id, b), update: (rid, b) => hr.updateReference(rid, b), remove: rid => hr.deleteReference(rid),
        });
      }
      const deptIds = [data.department_id, ...otherDepts].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
      if (!isNew || otherDepts.length) await hr.setEmployeeDepartments(empId, deptIds, data.department_id || null);
      // the record is saved; now the files, each through its own upload route
      const failed = [];
      const send = async (what, fn) => { try { await fn(); } catch (err) { failed.push(`${what}: ${errorText(err)}`); } };
      for (const { file, ...doc } of docs.filter(x => has(x.document_type) && has(x.title))) {
        const created = await hr.createDocument({ ...nonBlankAll(doc), employee_id: empId, requires_renewal: !!doc.requires_renewal });
        if (file instanceof File) await send(doc.title, () => hr.uploadDocumentFile(created.id, file));
      }
      if (files.photo) await send('Photo', () => hr.uploadPhoto(empId, files.photo));
      if (files.cv) await send('CV', () => hr.uploadCv(empId, files.cv));
      if (files.id_card) await send('ID document', () => hr.uploadIdDocument(empId, files.id_card));
      for (const f of files.certificate) await send(f.name, () => hr.uploadCertificate(empId, f, f.name.replace(/\.[^.]+$/, '')));
      for (const [type, label] of ONBOARDING_DOCS.filter(([t]) => !SLOT_ELSEWHERE.has(t) && files[t])) {
        await send(label, async () => {
          const doc = existingDocs.find(x => x.document_type === type) || await hr.createDocument({ employee_id: empId, document_type: type, title: label, requires_renewal: false });
          await hr.uploadDocumentFile(doc.id, files[type]);
        });
      }
      setFiles(NO_FILES);
      // submitting for approval opens the onboarding case; approving it activates the employee
      if (mode === 'approval' && (isNew || record?.employment_status === 'draft')) {
        await hr.createCase({ employee_id: empId, case_type: 'onboarding', title: `Onboarding — ${fullName}`, effective_date: data.hire_date || today() });
      }
      clearLookup('employees');
      notify(mode === 'draft' ? 'Draft saved' : mode === 'approval' ? `${fullName} submitted for onboarding approval` : isNew ? `${fullName} created` : 'Changes saved');
      setDocs([]);
      if (failed.length) {
        // the employee record is saved; stay here and say which files did not go up so they can be picked again
        setError(`Saved, but ${failed.length} file(s) were not uploaded — ${failed.join('; ')}`);
        if (isNew) setParams({ id: empId }, { replace: true });
        else { load(await hr.getEmployee(id)); await reloadFiles(); }
      } else if (isNew && mode === 'draft') setParams({ id: empId }, { replace: true });
      else if (mode !== 'draft') navigate(hrSubUrl('employee-360', { id: empId }));
      else { load(await hr.getEmployee(id)); await reloadFiles(); }
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  };

  if (!canManage) return <div className="panel"><div className="alert info">Creating and editing employee records is limited to HR managers (MD, DMD and administrators). Use Employee 360 to view records.</div></div>;
  if (!isNew && !record && !error) return <Loader title="Loading employee…" subtitle="Opening the employee record" />;

  const leaveRows = leaveTypes.filter(t => t.is_active && (t.applicable_gender === 'all' || !data.gender || t.applicable_gender === data.gender));
  const daily = data.salary_type === 'daily';
  const monthlyBase = daily ? (Number(data.daily_rate) || 0) * 22 : Number(data.basic_salary) || 0;
  const monthlyGross = monthlyBase + totalAllowances(data);
  const workDays = String(data.work_days || '').split(',').filter(Boolean);
  const bankTransfer = (data.payment_method || 'Bank Transfer') === 'Bank Transfer';

  return (
    <div className="wizard">
      <div className="panel wiz-head">
        <div className="grow">
          <div className="wiz-title">{isNew ? 'New employee' : fullName || record?.full_name} {record && <StatusPill value={record.employment_status} />}</div>
          <div className="wiz-progress"><span style={{ width: `${percent}%` }} /></div>
          <div className="muted small">{percent}% complete · {doneSteps.length} of {FORM_STEPS.length} sections done</div>
        </div>
        {onboarding && <button className="btn success" disabled={busy} onClick={() => save('draft')}><Save size={15} /> Save Draft</button>}
        <button className="btn danger" onClick={() => navigate(-1)}>Cancel</button>
      </div>

      {uploading && <div className="alert info">Uploading {uploading}…</div>}
      {uploadError && <div className="error">{uploadError}</div>}
      <div className="wiz-body">
        <ol className="panel wiz-steps">
          {STEPS.map((s, i) => {
            const st = stepState(i);
            return (
              <li key={s.key}>
                <button type="button" className={`wiz-step ${st}`} onClick={() => go(i)}>
                  <span className="wiz-dot">{st === 'done' ? <Check size={15} /> : st === 'missing' ? <CircleAlert size={15} /> : i + 1}</span>
                  <span className="wiz-step-text"><b>{s.title}</b><small>{stateLabel[st]}</small></span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="panel wiz-main">
          <div className="wiz-main-head">
            <span className="kpi-icon"><Icon name={current.icon} size={20} /></span>
            <div><h2>{current.heading || current.title}</h2><p className="muted">{current.desc}</p></div>
          </div>

          {current.key === 'personal' && (
            <>
              <PhotoCard load={() => hr.photoBlob(id)} storedKey={storedPhoto?.id} pending={files.photo}
                         onPick={f => pickFile('photo', f)} onClear={() => setFile('photo', null)} onDelete={() => removeStored(storedPhoto)}
                         onUpload={stagedUpload('photo')} busy={!!uploading} />
              <section className="wiz-section">
                <SectionHead icon="user-round" title="Basic Identity" hint="Legal name, gender, and date of birth" />
                {grid(['first_name', 'last_name', 'middle_name', 'full_name', 'gender', 'date_of_birth', 'nationality', 'state_of_origin', 'lga_of_origin',
                  'religion', 'marital_status', 'blood_group', 'genotype', 'employee_no'])}
                <div className="toggle-grid">
                  <Toggle title="Permanent Employee" desc="Long-term payroll eligible" checked={!!data.is_permanent} onChange={v => set('is_permanent', v)} />
                  <Toggle title="Confirmed Employee" desc={onboarding ? 'Past probation, fully confirmed' : 'Status changes through the employee workflow'}
                          checked={onboarding ? startAs === 'active' : record?.employment_status === 'active'} disabled={!onboarding}
                          onChange={v => setStartAs(v ? 'active' : 'probation')} />
                  <Toggle title="Private (Restricted Access)" desc="Limits who can view this record" checked={!!data.is_private} onChange={v => set('is_private', v)} />
                  <Toggle title="Expatriate Employee" desc="Foreign national; adds expatriate class and immigration details" checked={!!data.is_expatriate} onChange={v => set('is_expatriate', v)} />
                </div>
                {data.is_expatriate && grid(['expat_class', 'passport_number', 'passport_country', 'passport_expiry_date', 'visa_type', 'visa_expiry_date', 'cerpac_number', 'cerpac_expiry_date', 'quota_slot_reference'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="key-round" title="Login / System Access" hint="Work email is used for system login and payslip delivery" />
                {grid(['work_email', 'personal_email'], 2)}
              </section>
              <section className="wiz-section">
                <SectionHead icon="map-pin" title="Address Details" hint="Current and permanent residential address" />
                {grid(['residential_address', 'permanent_address', 'city', 'state_of_residence', 'country', 'postal_code'], 2)}
              </section>
              <section className="wiz-section">
                <SectionHead icon="id-card" title="Means of Identification" hint="Government-issued ID for background verification" />
                <div className="form-grid cols-3">
                  {['id_type', 'id_number', 'id_issue_date', 'id_expiry_date'].map(renderField)}
                  <div className="field">{docSlot('id_card', 'Upload ID Document')}</div>
                </div>
              </section>
            </>
          )}

          {current.key === 'company' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="building-2" title="Organizational Assignment" hint="Department, designation, unit and reporting office" />
                {grid(['department_id', 'designation_id', 'job_title', 'unit', 'section', 'team_number', 'branch_id', 'reporting_office', 'staff_category'])}
                <div className="field"><span className="field-label">Other Departments{record?.user_email ? ' — also applied to the linked login (approval routing)' : ''}</span>
                  <div className="toggle-grid">
                    {(options.departments || []).filter(dep => String(dep.id) !== String(data.department_id)).map(dep => {
                      const on = otherDepts.includes(String(dep.id));
                      return <Toggle key={dep.id} title={dep.name} desc={on ? 'Member' : 'Not a member'} checked={on}
                                     onChange={v => setOtherDepts(list => (v ? [...list, String(dep.id)] : list.filter(x => x !== String(dep.id))))} />;
                    })}
                  </div>
                </div>
              </section>
              <section className="wiz-section">
                <SectionHead icon="briefcase" title="Employment Details" hint="Employment terms and supervision" />
                {grid(['hire_date', 'date_of_join', 'employment_type', 'management_type', 'employee_level', 'reports_to_id'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="map-pin" title="Operational Assignment" hint="Primary project site and site access; extra sites are granted under Project Site Access" />
                {grid(['primary_site_id', 'company_site', 'site_access_level'])}
                <div className="toggle-grid">
                  <Toggle title="Head Office Based" desc="Works from the head office" checked={!!data.head_office_based} onChange={v => set('head_office_based', v)} />
                  <Toggle title="Covers All Sites" desc="May work on every project site" checked={!!data.cover_all_sites} onChange={v => set('cover_all_sites', v)} />
                </div>
              </section>
              <section className="wiz-section">
                <SectionHead icon="hourglass" title="Probation Details" hint="Probation plan, reviewer and confirmation" />
                {grid(['probation_start_date', 'probation_end_date', 'probation_months', 'probation_department_id', 'post_probation_department_id', 'probation_reviewer_id', 'confirmation_due_date', 'confirmation_date'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="hard-hat" title="PPE / Work Gear / Issue Items" hint="Items issued to the employee at onboarding" />
                <div className="toggle-grid">
                  {PPE_ITEMS.map(([n, text]) => <Toggle key={n} title={text} desc={data[n] ? 'Issued' : 'Not issued'} checked={!!data[n]} onChange={v => set(n, v)} />)}
                </div>
                {grid(['ppe_size', 'uniform_size', 'ppe_issue_date', 'ppe_issued_by', 'ppe_remarks'])}
              </section>
            </>
          )}

          {current.key === 'contact' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="phone" title="Contact Information" hint="Numbers HR and payroll use to reach the employee" />
                {grid(['work_phone', 'personal_phone', 'whatsapp_number', 'emergency_phone', 'preferred_contact'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="user-check" title="Professional References" hint={onboarding ? 'References 1 and 2 are required for onboarding; each is verified from Employee 360' : 'Up to three references or guarantors'} />
                <ListEditor rows={references} setRows={setReferences} columns={REFERENCE_COLS} addLabel="Add Reference" max={3} empty="No references added."
                            heading={(i, r) => `Professional Reference ${i + 1}${r.status && r.status !== 'pending' ? ` · ${titleCase(r.status)}` : ''}`} blankRow={{}} />
              </section>
              <section className="wiz-section">
                <SectionHead icon="siren" title="Emergency Contacts & Next of Kin" hint="People to contact in an emergency; mark the next of kin" />
                <ListEditor rows={contacts} setRows={setContacts} columns={CONTACT_COLS} addLabel="Add Contact" empty="No emergency contacts added."
                            blankRow={{ is_primary_contact: contacts.length === 0, is_next_of_kin: false }} />
              </section>
            </>
          )}

          {current.key === 'academic' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="graduation-cap" title="Academic & Qualifications" hint="Highest qualification, certifications and skills" />
                {grid(['highest_qualification', 'course_of_study', 'institution', 'graduation_year', 'professional_certification', 'skills'])}
                <div className="form-grid cols-2">{docSlot('cv', 'CV / Résumé')}{certificateSlot}</div>
              </section>
              <section className="wiz-section">
                <SectionHead icon="users" title="Family & Dependants" hint="Spouse, children and other dependants — dependants may be covered by the HMO; next of kin is marked under Emergency Contacts" />
                <ListEditor rows={dependants} setRows={setDependants} columns={DEPENDANT_COLS} addLabel="Add Dependant" empty="No dependants added." blankRow={{ hmo_covered: false }} />
              </section>
            </>
          )}

          {current.key === 'financial' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="settings-2" title="Salary Configuration" hint="How pay is set: from a salary grade, or configured manually with a reason" />
                {grid(['salary_type', 'salary_mode', 'employee_category', 'job_family', 'job_position', 'pay_grade', ...(data.salary_mode === 'manual' || outside ? ['grade_exception_reason'] : [])])}
                {rule && <div className="muted small" style={{ marginTop: 6 }}>Grade policy <b>{rule.policy_name}</b>: grades {rule.min_grade}–{rule.max_grade}{rule.default_grade ? `, default ${rule.default_grade}` : ''}.</div>}
                {outside && <div className="alert warn" style={{ marginTop: 8 }}>{outside} — give a grade exception reason to keep it.</div>}
              </section>
              {policy && policy.targets.length > 0 && (
                <section className="wiz-section">
                  <SectionHead icon="shield-check" title="Benefit Eligibility" hint={`From Payroll › Grade Policy Setup for ${data.job_family || 'every family'}${gradeOf(policy, data.pay_grade)?.level ? ` at grade level ${gradeOf(policy, data.pay_grade).level}` : ''}`} />
                  <BenefitTable rows={benefitRows(policy, data)} />
                </section>
              )}
              <section className="wiz-section">
                <SectionHead icon="wallet" title="Salary Amounts" hint={daily ? 'Daily-paid: the monthly figure uses 22 working days' : 'Monthly base salary; later raises are recorded as Salary Changes on Employee 360'} />
                {grid([daily ? 'daily_rate' : 'basic_salary', ...(daily ? ['basic_salary'] : []), 'next_review_date'])}
                <div className="toggle-grid">
                  <Toggle title="Salary On Review" desc="Flag the salary for the next review cycle" checked={!!data.salary_on_review} onChange={v => set('salary_on_review', v)} />
                </div>
              </section>
              <section className="wiz-section">
                <SectionHead icon="circle-plus" title="Allowances" hint="Monthly allowances paid on top of the base" />
                {grid(ALLOWANCES)}
              </section>
              <section className="wiz-section">
                <SectionHead icon="timer" title="Overtime & Bonus Eligibility" />
                <div className="toggle-grid">
                  <Toggle title="Overtime Eligible" desc="Paid for authorised overtime" checked={!!data.overtime_eligible} onChange={v => set('overtime_eligible', v)} />
                  <Toggle title="Bonus Eligible" desc="Included in bonus and performance pay" checked={!!data.bonus_eligible} onChange={v => set('bonus_eligible', v)} />
                  <Toggle title="Leave Allowance Eligible" desc="Receives the annual leave allowance" checked={!!data.leave_allowance_eligible} onChange={v => set('leave_allowance_eligible', v)} />
                </div>
                {data.overtime_eligible && grid(['overtime_rate', 'overtime_threshold_hours'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="landmark" title="Pension & Tax Setup" hint="Statutory contribution rates (Pension Reform Act defaults: 8% employee, 10% employer)" />
                <div className="toggle-grid">
                  <Toggle title="Include in Pension" desc="Deduct and remit pension" checked={!!data.include_in_pension} onChange={v => set('include_in_pension', v)} />
                  <Toggle title="Include in PAYE" desc="Compute and remit PAYE tax" checked={!!data.include_in_paye} onChange={v => set('include_in_paye', v)} />
                </div>
                {data.include_in_pension && grid(['pension_employee_rate', 'pension_employer_rate'])}
              </section>
              <div className="review-hero">
                <div className="grow">
                  <div className="hero-name">{fmtMoney(monthlyGross)}</div>
                  <div className="hero-sub">Estimated monthly gross — base {fmtMoney(monthlyBase)} + allowances {fmtMoney(totalAllowances(data))}</div>
                </div>
                <div className="hero-facts"><span className="hero-fact">Annual: <b>{fmtMoney(monthlyGross * 12)}</b></span></div>
              </div>
            </>
          )}

          {current.key === 'payment' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="landmark" title="Payment Method" hint={bankTransfer ? 'Salary is paid by bank transfer to this account' : 'Paid outside the bank run — no account required'} />
                {grid(['payment_method', ...(bankTransfer ? ['bank_name', 'bank_account_name', 'bank_account_number', 'bank_branch', 'bank_sort_code', 'bvn'] : [])])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="piggy-bank" title="Pension Details" />
                {grid(['pension_fund_administrator', 'pension_rsa_pin'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="file-check" title="Statutory & Regulatory Numbers" />
                {grid(['tax_identification_number', 'nhf_number', 'nhia_number', 'nsitf_number', 'itf_number', 'hmo_provider', 'hmo_id'])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="notebook-pen" title="Internal HR Notes" hint="Visible to HR only" />
                {grid(['hr_notes', 'onboarding_remark', 'payroll_remark'])}
              </section>
            </>
          )}

          {current.key === 'work' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="sliders-horizontal" title="Work Rules Control" hint="Follow the salary grade policy, or set the rules manually with a reason" />
                {grid(['work_rules_source', ...(data.work_rules_source === 'manual' ? ['work_rules_override_reason'] : [])])}
              </section>
              <section className="wiz-section">
                <SectionHead icon="clock" title="Shift & Work Schedule" />
                {grid(['shift_name', 'shift_type', 'work_pattern', 'days_per_week', 'daily_hours', 'attendance_method', 'shift_start', 'shift_end'])}
                <div className="field"><span className="field-label">Work Days</span>
                  <div className="toggle-grid">
                    {WEEKDAYS.map(dname => {
                      const on = workDays.includes(dname);
                      return <Toggle key={dname} title={dname} desc={on ? 'Working day' : 'Off'} checked={on}
                                     onChange={v => set('work_days', WEEKDAYS.filter(x => (x === dname ? v : workDays.includes(x))).join(','))} />;
                    })}
                  </div>
                </div>
              </section>
              <section className="wiz-section">
                <div className="toolbar">
                  <SectionHead icon="calendar-days" title="Leave Entitlements" hint="Days per year for this employee. Balances are allocated when the leave year is initialised in Leave Management." />
                  <div className="spacer" />
                  {leaveTypes.length > 0 && <button type="button" className="btn" onClick={applyLeavePolicy}><Wand2 size={15} /> Use Leave Policy</button>}
                </div>
                {grid(['annual_leave_days', 'sick_leave_days', 'casual_leave_days', 'maternity_leave_days', 'paternity_leave_days', 'compassionate_leave_days', 'leave_accrual_method', 'max_carry_forward_days'])}
                <div className="toggle-grid">
                  <Toggle title="Carry Forward Allowed" desc="Unused annual leave rolls into next year" checked={!!data.carry_forward_allowed} onChange={v => set('carry_forward_allowed', v)} />
                </div>
                {leaveRows.length > 0 && (
                  <div className="table-wrap"><table className="e360-table">
                    <thead><tr><th>Company Leave Policy</th><th>Days / Year</th><th>Credit</th><th>Paid</th><th>Carry Forward (max)</th></tr></thead>
                    <tbody>{leaveRows.map(t => (
                      <tr key={t.id}><td><b>{t.name}</b></td><td className="num">{t.days_allowed}</td><td>{titleCase(t.credit_type)}</td><td>{t.is_paid ? 'Yes' : 'No'}</td><td className="num">{t.max_carry_forward}</td></tr>
                    ))}</tbody>
                  </table></div>
                )}
              </section>
            </>
          )}

          {current.key === 'documents' && (
            <>
              <section className="wiz-section">
                <SectionHead icon="folder-check" title="Onboarding Documents" hint="Each document takes only its own file types; files upload when you save" />
                <div className="doc-grid">
                  {ONBOARDING_DOCS.map(([type, text, icon]) => {
                    const onFile = storedOf(type);
                    const recorded = existingDocs.filter(x => x.document_type === type);
                    const waiting = type === 'certificate' ? files.certificate.length > 0 : !!files[type];
                    const state = onFile.length ? 'done' : waiting ? 'waiting' : '';
                    return (
                      <div key={type} className={`doc-card ${state}`}>
                        <div className="doc-card-head">
                          <span className="sec-icon"><Icon name={icon} size={17} /></span>
                          <b>{text}</b>
                          <span className={`ref-tag ${onFile.length ? 'done' : waiting ? 'req' : ''}`}>{onFile.length ? 'On file' : waiting ? 'On save' : recorded.length ? 'No scan' : 'Pending'}</span>
                        </div>
                        {type === 'certificate' ? certificateSlot : docSlot(type)}
                      </div>
                    );
                  })}
                </div>
              </section>
              <section className="wiz-section">
                <SectionHead icon="calendar-clock" title="Documents with Expiry Dates" hint="Passports, permits and licences — expiry dates feed the Document Expiry Dashboard" />
                <ListEditor rows={docs} setRows={setDocs} columns={DOC_COLS} addLabel="Add Document" empty="No new documents to add." blankRow={{ requires_renewal: false }} />
              </section>
            </>
          )}

          {current.key === 'review' && (
            <>
              {allMissing.length > 0 ? (
                <div className="review-missing">
                  <b><Icon name="triangle-alert" size={16} /> {allMissing.length} required field(s) missing</b>
                  <div className="missing-chips">
                    {allMissing.map(m => <button key={m.name} type="button" className="missing-chip" title={`Go to ${STEPS[m.step].title}`} onClick={() => go(m.step)}>{F[m.name].label}</button>)}
                  </div>
                </div>
              ) : (
                <div className="alert good-alert"><b>All required fields are complete.</b> Review the summary below, then choose how to submit.</div>
              )}
              <div className="review-hero">
                <div className="hero-avatar">{initials(fullName) || '?'}</div>
                <div className="grow">
                  <div className="hero-name">{fullName || 'Employee Name'}</div>
                  <div className="hero-sub">{label('designation_id', data.designation_id) || data.job_title || '—'} · {label('department_id', data.department_id) || '—'}</div>
                  <div className="hero-sub">{titleCase(data.employment_type) || '—'} · {isNew ? 'New — not saved yet' : record.employment_status === 'onboarding' ? 'Onboarding (awaiting approval)' : titleCase(record.employment_status)}</div>
                </div>
                <div className="hero-facts">
                  <span className="hero-fact">ID: <b>{data.employee_no || 'Auto'}</b></span>
                  {data.pay_grade && <span className="hero-fact">Grade: <b>{data.pay_grade}</b></span>}
                  <span className="hero-fact">Profile: <b>{percent}% complete</b></span>
                </div>
              </div>
              <div className="review-grid">
                {[
                  ['personal', 'Personal Details', 'user-round', [['Full Name', fullName], ['Gender', label('gender', data.gender)], ['Date of Birth', label('date_of_birth', data.date_of_birth)],
                    ['Nationality', data.nationality], ['Marital Status', label('marital_status', data.marital_status)], ['State of Origin', data.state_of_origin],
                    ['Work Email', data.work_email], ['ID Type', data.id_type], ['ID Number', data.id_number]]],
                  ['company', 'Company Details', 'building-2', [['Department', label('department_id', data.department_id)], ['Designation', label('designation_id', data.designation_id)],
                    ['Job Title', data.job_title], ['Employment Type', label('employment_type', data.employment_type)], ['Date of Employment', label('hire_date', data.hire_date)],
                    ['Branch', label('branch_id', data.branch_id)], ['Primary Site', label('primary_site_id', data.primary_site_id)], ['Management Type', data.management_type],
                    ['Supervisor', label('reports_to_id', data.reports_to_id)], ['Probation Ends', label('probation_end_date', data.probation_end_date)]]],
                  ['contact', 'Contact & References', 'phone', [['Phone 1', data.work_phone], ['Phone 2', data.personal_phone], ['WhatsApp', data.whatsapp_number],
                    ['Emergency', data.emergency_phone], ['Reference 1', references[0]?.full_name], ['Ref 1 Phone', references[0]?.phone],
                    ['Reference 2', references[1]?.full_name], ['Ref 2 Phone', references[1]?.phone], ['Next of Kin', contacts.find(c => c.is_next_of_kin)?.contact_name]]],
                  ['financial', 'Financial Details', 'wallet', [['Salary Type', label('salary_type', data.salary_type)], ['Salary Mode', label('salary_mode', data.salary_mode)],
                    ['Salary Grade', data.pay_grade], [daily ? 'Daily Rate' : 'Base Salary', label(daily ? 'daily_rate' : 'basic_salary', daily ? data.daily_rate : data.basic_salary)],
                    ['Housing Allowance', label('housing_allowance', data.housing_allowance)], ['Transport Allowance', label('transport_allowance', data.transport_allowance)],
                    ['Est. Monthly Gross', fmtMoney(monthlyGross)], ['Overtime Eligible', data.overtime_eligible ? 'Yes' : 'No'],
                    ['Pension Eligible', data.include_in_pension ? 'Yes' : 'No'], ['Tax Eligible', data.include_in_paye ? 'Yes' : 'No'],
                    ['On Review', data.salary_on_review ? `Yes${data.next_review_date ? ` — ${fmtDate(data.next_review_date)}` : ''}` : 'No']]],
                  ['payment', 'Payment & Statutory', 'landmark', [['Payment Method', data.payment_method], ['Bank', data.bank_name], ['Account Number', data.bank_account_number],
                    ['Pension Provider', data.pension_fund_administrator], ['TIN', data.tax_identification_number], ['HMO ID', data.hmo_id]]],
                  ['work', 'Work Rules & Leave', 'calendar-clock', [['Policy Source', label('work_rules_source', data.work_rules_source)], ['Shift Type', data.shift_type],
                    ['Attendance', data.attendance_method], ['Days/Week', data.days_per_week], ['Annual Leave', has(data.annual_leave_days) ? `${data.annual_leave_days} days` : null],
                    ['Sick Leave', has(data.sick_leave_days) ? `${data.sick_leave_days} days` : null], ['Casual Leave', has(data.casual_leave_days) ? `${data.casual_leave_days} days` : null],
                    ['Leave Allowance', data.leave_allowance_eligible ? 'Yes' : 'No']]],
                  ['academic', 'Academic & Family', 'graduation-cap', [['Qualification', data.highest_qualification], ['Course', data.course_of_study],
                    ['Institution', data.institution], ['Year', data.graduation_year], ['Dependants', dependants.length ? `${dependants.length} on file` : null]]],
                  ['documents', 'Documents', 'file-badge', [['On File', existingDocs.length ? `${existingDocs.length} document(s)` : null], ['To Add', docs.length ? `${docs.length} document(s)` : null]]],
                ].map(([key, title, icon, rows]) => (
                  <div key={key} className="review-card">
                    <div className="review-card-head">
                      <span className="sec-icon"><Icon name={icon} size={16} /></span>
                      <b>{title}</b>
                      <button type="button" className="btn ghost small" onClick={() => go(STEPS.findIndex(s => s.key === key))}><Pencil size={13} /> Edit</button>
                    </div>
                    <dl>{rows.map(([l, v]) => <div key={l}><dt>{l}</dt><dd className={has(v) ? '' : 'missing'}>{has(v) ? v : 'Not provided'}</dd></div>)}</dl>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && <div className="error">{error}</div>}

          <div className="wiz-nav">
            <button className="btn" disabled={step === 0} onClick={() => go(step - 1)}><ArrowLeft size={15} /> Previous</button>
            <div className="spacer" />
            {step < STEPS.length - 1
              ? <button className="btn primary" onClick={() => go(step + 1)}>Next <ArrowRight size={15} /></button>
              : onboarding ? (
                <>
                  <button className="btn" disabled={busy} onClick={() => save('approval')}><Send size={15} /> Submit for Approval</button>
                  <button className="btn primary" disabled={busy} onClick={() => save('submit')}><Check size={15} /> {busy ? 'Saving…' : 'Create Employee'}</button>
                </>
              ) : (
                <button className="btn primary" disabled={busy} onClick={() => save('submit')}><Send size={15} /> {busy ? 'Saving…' : 'Save Changes'}</button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

// child row -> payload: drop server-managed keys and blank strings
function nonBlankAll(row) {
  const { id, employee_id, created_at, updated_at, status, verified_at, verified_by_user_id, employee_name, employee_no, ...rest } = row;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v === '' ? null : v]));
}

// apply the edited list of child rows: rows gone from the list are deleted, rows without a server id are added,
// rows whose fields changed are updated
async function syncList(before, after, { add, update, remove }) {
  for (const b of before) if (!after.some(a => a.id === b.id)) await remove(b.id);
  for (const a of after) {
    if (!a.id) { await add(nonBlankAll(a)); continue; }
    const b = before.find(x => x.id === a.id);
    if (b && JSON.stringify(nonBlankAll(b)) !== JSON.stringify(nonBlankAll(a))) await update(a.id, nonBlankAll(a));
  }
}

// the employee's benefit eligibility (Grade Policy Setup › Benefit Eligibility Rules)
export function BenefitTable({ rows }) {
  const tone = { Eligible: statusTone('approved'), 'Not Eligible': statusTone('rejected'), 'Requires Approval': statusTone('pending') };
  return (
    <div className="table-wrap"><table className="register-table">
      <thead><tr><th>Benefit / Allowance</th><th>Eligibility</th><th>Note</th></tr></thead>
      <tbody>{rows.map(r => <tr key={r.benefit}><td>{r.benefit}</td><td><span className={`pill tone-${tone[r.status] || 'muted'}`}>{r.status}</span></td><td className="muted small">{r.note}</td></tr>)}</tbody>
    </table></div>
  );
}
